import { Alert, Button, Card, Statistic } from "antd";
import { useEffect, useState } from "react";
import { DateRangePicker as ZhDateRangePicker } from "zhihao-ui-react";

import type {
    MemberCustomerVo,
    MemberRevenueStatisticVo,
    PurchaseOverviewVo,
    ServiceCostIncomeVo,
    ShippingDemandStatisticVo,
    HotShipRouteVo,
    HotShipDestinationPortVo,
    ShipStatisticVo,
    ShipInfoServiceFeeStatisticVo
} from "@zhihaoscm/entity";
import type { RegisterCustomerVo } from "@/entity/dashboard";
import { formatThousandSeparator } from "@/utils/money";

type DashboardStatisticParams = {
    scope?: number;
    beginTime?: string;
    endTime?: string;
};

type PurchaseOverviewParams = {
    scope?: string | number;
    beginTime?: string;
    endTime?: string;
};

type PurchaseServiceCostParams = {
    scope?: number;
    beiginTime?: string;
    endTime?: string;
};

type PurchaseServiceCostAdditionParams = {
    statisticsScope?: string | number;
    statisticsBeiginTime?: string;
    statisticsEndTime?: string;
};

type PurchaseServiceCostShapeParams = {
    pieChartScope?: number;
    pieChartBeiginTime?: string;
    pieChartEndTime?: string;
};

type PurchaseServiceCostAdditionAnalysisParams = {
    analysisScope?: string | number;
    analysisMonth?: string;
};

type AddMemberCustomerStatisticParams = {
    scope?: string | number;
    beginTime?: string;
    endTime?: string;
};

type ShippingDemandStatisticParams = {
    scope?: number;
    beginTime?: string;
    endTime?: string;
};

type AddShippingDemandStatisticParams = {
    scope?: string;
    beginTime?: string;
    endTime?: string;
};

type ShippingDemandConvertParams = {
    scope?: string | number;
    beginTime?: string;
    endTime?: string;
};

type ShipStatisticParams = {
    scope?: number;
    beginTime?: string;
    endTime?: string;
};

type AddShipStatisticParams = {
    scope?: string;
    beginTime?: string;
    endTime?: string;
};

type AddShippingDemandAnalysisParams = {
    scope?: string | number;
    date?: string;
};

type ShipOrderDataAnalysisParams = {
    scope?: string | number;
    date?: string;
};

type HotShipRouteRankParams = {
    beginTime?: string;
    endTime?: string;
};

type HotShipDestinationPortRankParams = {
    beginTime?: string;
    endTime?: string;
};

type AddShipInfoServiceFeeAnalysisParams = {
    scope?: string | number;
    date?: string;
};

type HomeDateFilter = Pick<DashboardStatisticParams, "scope" | "beginTime" | "endTime">;

interface HomeDateFilterControlProps {
    testId: string;
    onChange: (filter: HomeDateFilter) => void;
}

const HOME_DATE_SHORTCUTS = [
    { label: "全部", scope: 1 },
    { label: "近一月", scope: 2 },
    { label: "近一季度", scope: 3 },
    { label: "近一年", scope: 4 }
] as const;

/** 沿用 Vue date-select 的快捷范围和自定义日期范围，统一输出旧接口字段。 */
function HomeDateFilterControl({ testId, onChange }: HomeDateFilterControlProps) {
    return (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-1" data-testid={testId}>
            {HOME_DATE_SHORTCUTS.map(item => (
                <Button key={item.scope} size="small" type="text" onClick={() => onChange({ scope: item.scope })}>
                    {item.label}
                </Button>
            ))}
            <ZhDateRangePicker
                aria-label="自定义日期范围"
                clearable={false}
                format="YYYY-MM-DD"
                startPlaceholder="开始日期"
                endPlaceholder="结束日期"
                rangeSeparator="-"
                valueFormat="YYYY-MM-DD"
                onChange={(_, formattedValue) =>
                    onChange(formattedValue ? { beginTime: formattedValue[0], endTime: formattedValue[1] } : {})
                }
            />
        </div>
    );
}

export interface HomePageProps {
    loadPurchaseOverview?: (params: PurchaseOverviewParams) => Promise<Partial<PurchaseOverviewVo>>;
    loadPurchaseServiceCost?: (params: PurchaseServiceCostParams) => Promise<Partial<ServiceCostIncomeVo>>;
    loadPurchaseServiceCostAdditionStatistics?: (
        params: PurchaseServiceCostAdditionParams
    ) => Promise<Partial<ServiceCostIncomeVo>>;
    loadPurchaseServiceCostShape?: (params: PurchaseServiceCostShapeParams) => Promise<Partial<ServiceCostIncomeVo>>;
    loadPurchaseServiceCostAdditionAnalysis?: (
        params: PurchaseServiceCostAdditionAnalysisParams
    ) => Promise<Partial<ServiceCostIncomeVo>>;
    loadAddMemberCustomerStatistic?: (params: AddMemberCustomerStatisticParams) => Promise<Partial<MemberCustomerVo>>;
    loadShippingDemandStatistic?: (
        params: ShippingDemandStatisticParams
    ) => Promise<Partial<ShippingDemandStatisticVo>>;
    loadAddShippingDemandStatistic?: (
        params: AddShippingDemandStatisticParams
    ) => Promise<Partial<ShippingDemandStatisticVo>>;
    loadShippingDemandConvertAnalysis?: (
        params: ShippingDemandConvertParams
    ) => Promise<Partial<ShippingDemandStatisticVo>>;
    loadShipStatistic?: (params: ShipStatisticParams) => Promise<Partial<ShipStatisticVo>>;
    loadAddShipStatistic?: (params: AddShipStatisticParams) => Promise<Partial<ShipStatisticVo>>;
    loadAddShippingDemandAnalysis?: (
        params: AddShippingDemandAnalysisParams
    ) => Promise<Partial<ShippingDemandStatisticVo>>;
    loadShipOrderDataAnalysis?: (params: ShipOrderDataAnalysisParams) => Promise<Partial<ShippingDemandStatisticVo>>;
    loadHotShipRouteRank?: (params: HotShipRouteRankParams) => Promise<HotShipRouteVo[]>;
    loadHotShipDestinationPortRank?: (params: HotShipDestinationPortRankParams) => Promise<HotShipDestinationPortVo[]>;
    loadShipInfoServiceFeeStatistic?: (params: ShipStatisticParams) => Promise<Partial<ShipInfoServiceFeeStatisticVo>>;
    loadAddShipInfoServiceFeeAnalysis?: (
        params: AddShipInfoServiceFeeAnalysisParams
    ) => Promise<Partial<ShipInfoServiceFeeStatisticVo>>;
    loadRegisteredCustomerStatistic?: (params?: DashboardStatisticParams) => Promise<Partial<RegisterCustomerVo>>;
    loadMemberCustomerStatistic?: (params?: DashboardStatisticParams) => Promise<Partial<MemberCustomerVo>>;
    loadMemberRevenueStatistic?: (params?: DashboardStatisticParams) => Promise<Partial<MemberRevenueStatisticVo>>;
    loadCustomerConvertAnalysis?: (params?: DashboardStatisticParams) => Promise<Partial<MemberCustomerVo>>;
    loadAddRegisteredCustomerAnalysis?: (params?: DashboardStatisticParams) => Promise<Partial<RegisterCustomerVo>>;
    loadAddMemberRevenueAnalysis?: (params?: DashboardStatisticParams) => Promise<Partial<MemberRevenueStatisticVo>>;
    loadAddMemberCustomerAnalysis?: (params?: DashboardStatisticParams) => Promise<Partial<MemberCustomerVo>>;
}

/** 延迟加载 Vue 砂石交易总览接口，保持采购看板原有的统计参数协议。 */
const loadPurchaseDashboardOverview = async (params: PurchaseOverviewParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getPurchaseOverview(params);
};

/** 延迟加载 Vue 服务费收入统计接口，保留历史参数命名和默认全量范围。 */
const loadPurchaseDashboardServiceCost = async (params: PurchaseServiceCostParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getPurchaseServiceCost(params);
};

/** 延迟加载 Vue 服务费新增统计接口，默认使用近一月统计范围。 */
const loadPurchaseDashboardServiceCostAdditionStatistics = async (params: PurchaseServiceCostAdditionParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getPurchaseServiceCost(params);
};

/** 延迟加载 Vue 服务费构成分析接口，保留环形图原有的查询参数协议。 */
const loadPurchaseDashboardServiceCostShape = async (params: PurchaseServiceCostShapeParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getPurchaseServiceCost(params);
};

/** 延迟加载 Vue 服务费新增分析接口，默认使用近一月分析范围。 */
const loadPurchaseDashboardServiceCostAdditionAnalysis = async (params: PurchaseServiceCostAdditionAnalysisParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getPurchaseServiceCost(params);
};

/** 延迟加载 Vue 会员用户新增统计接口，默认使用近一月统计范围。 */
const loadAddedMemberCustomerDashboardStatistic = async (params: AddMemberCustomerStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddMemberCustomerStatistic(params);
};

/** 延迟加载 Vue 船运需求统计接口，保留首屏空参数请求和筛选参数协议。 */
const loadShippingDashboardDemandStatistic = async (params: ShippingDemandStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getShippingDemandStatistic(params);
};

/** 延迟加载 Vue 新增船运需求统计接口，默认使用近一月范围。 */
const loadAddedShippingDashboardDemandStatistic = async (params: AddShippingDemandStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddShippingDemandStatistic(params);
};

/** 延迟加载 Vue 船运需求转化分析接口，保留默认全量统计范围。 */
const loadShippingDashboardDemandConvertAnalysis = async (params: ShippingDemandConvertParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getShippingDemandConvertAnalysis(params);
};

/** 延迟加载 Vue 船舶统计接口，保留首屏空参数请求和筛选参数协议。 */
const loadShipDashboardStatistic = async (params: ShipStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getShipStatistic(params);
};

/** 延迟加载 Vue 船舶新增统计接口，默认使用近一月范围并保持字符串 scope。 */
const loadAddedShipDashboardStatistic = async (params: AddShipStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddShipStatistic(params);
};

/** 延迟加载 Vue 船运需求新增分析接口，默认使用近一月范围。 */
const loadAddedShippingDemandDashboardAnalysis = async (params: AddShippingDemandAnalysisParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddShippingDemandAnalysis(params);
};

/** 延迟加载 Vue 船运单数据分析接口，默认使用近一季度范围。 */
const loadShipOrderDashboardDataAnalysis = async (params: ShipOrderDataAnalysisParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getShipOrderDataAnalysis(params);
};

/** 延迟加载 Vue 热门航线排行接口，保留首屏空参数请求。 */
const loadHotShipRouteDashboardRank = async (params: HotShipRouteRankParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getHotShipRouteRank(params);
};

/** 延迟加载 Vue 热门目的地排行接口，保留首屏空参数请求。 */
const loadHotShipDestinationPortDashboardRank = async (params: HotShipDestinationPortRankParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getHotShipDestinationPortRank(params);
};

/** 延迟加载 Vue 船务信息服务费统计接口，保留首屏空参数请求。 */
const loadShipInfoServiceFeeDashboardStatistic = async (params: ShipStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getShipInfoServiceFeeStatistic(params);
};

/** 延迟加载 Vue 船务信息服务费新增分析接口，默认使用近一季度范围。 */
const loadAddedShipInfoServiceFeeDashboardAnalysis = async (params: AddShipInfoServiceFeeAnalysisParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddShipInfoServiceFeeAnalysis(params);
};

/** 延迟加载看板服务，避免公开路由契约测试和首页静态路由初始化触发 API 依赖链。 */
const loadDashboardStatistic = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getRegisteredCustomerStatistic(params);
};

/** 延迟加载 Vue 会员用户统计接口，保持首页首屏路由初始化轻量。 */
const loadMemberDashboardStatistic = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getMemberCustomerStatistic(params);
};

/** 延迟加载 Vue 会员营收统计接口，保持首页首屏路由初始化轻量。 */
const loadMemberRevenueDashboardStatistic = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getMemberRevenueStatistic(params);
};

/** 延迟加载 Vue 用户转化分析接口，并保留默认全量统计范围。 */
const loadCustomerConversionDashboardAnalysis = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getCustomerConvertAnalysis(params || { scope: 0 });
};

/** 延迟加载 Vue 新增注册用户分析接口，保留首屏默认统计范围。 */
const loadAddedRegistrationDashboardAnalysis = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddRegisteredCustomerAnalysis(params || { scope: 0 });
};

/** 延迟加载 Vue 新增会员费用分析接口，保留首屏默认统计范围。 */
const loadAddedMemberRevenueDashboardAnalysis = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddMemberRevenueAnalysis(params || { scope: 0 });
};

/** 延迟加载 Vue 新增会员用户分析接口，保留首屏默认统计范围。 */
const loadAddedMemberCustomerDashboardAnalysis = async (params?: DashboardStatisticParams) => {
    const { dashboardService } = await import("@/api/services/dashboardService");
    return dashboardService.getAddMemberCustomerAnalysis(params || { scope: 0 });
};

/** 管理后台首页，先迁移 Vue 链云用户看板的首屏统计和错误状态。 */
export function HomePage({
    loadPurchaseOverview = loadPurchaseDashboardOverview,
    loadPurchaseServiceCost = loadPurchaseDashboardServiceCost,
    loadPurchaseServiceCostAdditionStatistics = loadPurchaseDashboardServiceCostAdditionStatistics,
    loadPurchaseServiceCostShape = loadPurchaseDashboardServiceCostShape,
    loadPurchaseServiceCostAdditionAnalysis = loadPurchaseDashboardServiceCostAdditionAnalysis,
    loadAddMemberCustomerStatistic = loadAddedMemberCustomerDashboardStatistic,
    loadShippingDemandStatistic = loadShippingDashboardDemandStatistic,
    loadAddShippingDemandStatistic = loadAddedShippingDashboardDemandStatistic,
    loadShippingDemandConvertAnalysis = loadShippingDashboardDemandConvertAnalysis,
    loadShipStatistic = loadShipDashboardStatistic,
    loadAddShipStatistic = loadAddedShipDashboardStatistic,
    loadAddShippingDemandAnalysis = loadAddedShippingDemandDashboardAnalysis,
    loadShipOrderDataAnalysis = loadShipOrderDashboardDataAnalysis,
    loadHotShipRouteRank = loadHotShipRouteDashboardRank,
    loadHotShipDestinationPortRank = loadHotShipDestinationPortDashboardRank,
    loadShipInfoServiceFeeStatistic = loadShipInfoServiceFeeDashboardStatistic,
    loadAddShipInfoServiceFeeAnalysis = loadAddedShipInfoServiceFeeDashboardAnalysis,
    loadMemberCustomerStatistic = loadMemberDashboardStatistic,
    loadMemberRevenueStatistic = loadMemberRevenueDashboardStatistic,
    loadCustomerConvertAnalysis = loadCustomerConversionDashboardAnalysis,
    loadAddRegisteredCustomerAnalysis = loadAddedRegistrationDashboardAnalysis,
    loadAddMemberRevenueAnalysis = loadAddedMemberRevenueDashboardAnalysis,
    loadAddMemberCustomerAnalysis = loadAddedMemberCustomerDashboardAnalysis,
    loadRegisteredCustomerStatistic = loadDashboardStatistic
}: HomePageProps = {}) {
    const [purchaseOverview, setPurchaseOverview] = useState<Partial<PurchaseOverviewVo>>();
    const [purchaseServiceCost, setPurchaseServiceCost] = useState<Partial<ServiceCostIncomeVo>>();
    const [purchaseServiceCostAdditionStatistics, setPurchaseServiceCostAdditionStatistics] =
        useState<Partial<ServiceCostIncomeVo>>();
    const [purchaseServiceCostShape, setPurchaseServiceCostShape] = useState<Partial<ServiceCostIncomeVo>>();
    const [purchaseServiceCostAdditionAnalysis, setPurchaseServiceCostAdditionAnalysis] =
        useState<Partial<ServiceCostIncomeVo>>();
    const [addedMemberCustomerStatistic, setAddedMemberCustomerStatistic] = useState<Partial<MemberCustomerVo>>();
    const [shippingDemandStatistic, setShippingDemandStatistic] = useState<Partial<ShippingDemandStatisticVo>>();
    const [addedShippingDemandStatistic, setAddedShippingDemandStatistic] =
        useState<Partial<ShippingDemandStatisticVo>>();
    const [shippingDemandConvertAnalysis, setShippingDemandConvertAnalysis] =
        useState<Partial<ShippingDemandStatisticVo>>();
    const [shipStatistic, setShipStatistic] = useState<Partial<ShipStatisticVo>>();
    const [addedShipStatistic, setAddedShipStatistic] = useState<Partial<ShipStatisticVo>>();
    const [addedShippingDemandAnalysis, setAddedShippingDemandAnalysis] =
        useState<Partial<ShippingDemandStatisticVo>>();
    const [shipOrderDataAnalysis, setShipOrderDataAnalysis] = useState<Partial<ShippingDemandStatisticVo>>();
    const [hotShipRouteRank, setHotShipRouteRank] = useState<HotShipRouteVo[]>([]);
    const [hotShipDestinationPortRank, setHotShipDestinationPortRank] = useState<HotShipDestinationPortVo[]>([]);
    const [shipInfoServiceFeeStatistic, setShipInfoServiceFeeStatistic] =
        useState<Partial<ShipInfoServiceFeeStatisticVo>>();
    const [addedShipInfoServiceFeeAnalysis, setAddedShipInfoServiceFeeAnalysis] =
        useState<Partial<ShipInfoServiceFeeStatisticVo>>();
    const [statistics, setStatistics] = useState<Partial<RegisterCustomerVo>>();
    const [memberStatistics, setMemberStatistics] = useState<Partial<MemberCustomerVo>>();
    const [memberRevenueStatistics, setMemberRevenueStatistics] = useState<Partial<MemberRevenueStatisticVo>>();
    const [conversionStatistics, setConversionStatistics] = useState<Partial<MemberCustomerVo>>();
    const [addedRegistrationStatistics, setAddedRegistrationStatistics] = useState<Partial<RegisterCustomerVo>>();
    const [addedMemberRevenueStatistics, setAddedMemberRevenueStatistics] =
        useState<Partial<MemberRevenueStatisticVo>>();
    const [addedMemberCustomerStatistics, setAddedMemberCustomerStatistics] = useState<Partial<MemberCustomerVo>>();
    const [loading, setLoading] = useState(true);
    const [memberLoading, setMemberLoading] = useState(true);
    const [memberRevenueLoading, setMemberRevenueLoading] = useState(true);
    const [conversionLoading, setConversionLoading] = useState(true);
    const [addedRegistrationLoading, setAddedRegistrationLoading] = useState(true);
    const [addedMemberRevenueLoading, setAddedMemberRevenueLoading] = useState(true);
    const [addedMemberCustomerLoading, setAddedMemberCustomerLoading] = useState(true);
    const [registeredDateFilter, setRegisteredDateFilter] = useState<HomeDateFilter>();
    const [memberDateFilter, setMemberDateFilter] = useState<HomeDateFilter>();
    const [revenueDateFilter, setRevenueDateFilter] = useState<HomeDateFilter>();
    const [conversionDateFilter, setConversionDateFilter] = useState<HomeDateFilter>();
    const [purchaseOverviewLoading, setPurchaseOverviewLoading] = useState(true);
    const [purchaseServiceCostLoading, setPurchaseServiceCostLoading] = useState(true);
    const [purchaseServiceCostAdditionLoading, setPurchaseServiceCostAdditionLoading] = useState(true);
    const [purchaseServiceCostShapeLoading, setPurchaseServiceCostShapeLoading] = useState(true);
    const [purchaseServiceCostAdditionAnalysisLoading, setPurchaseServiceCostAdditionAnalysisLoading] = useState(true);
    const [addedMemberCustomerStatisticLoading, setAddedMemberCustomerStatisticLoading] = useState(true);
    const [shippingDemandStatisticLoading, setShippingDemandStatisticLoading] = useState(true);
    const [addedShippingDemandStatisticLoading, setAddedShippingDemandStatisticLoading] = useState(true);
    const [shippingDemandConvertLoading, setShippingDemandConvertLoading] = useState(true);
    const [shipStatisticLoading, setShipStatisticLoading] = useState(true);
    const [addedShipStatisticLoading, setAddedShipStatisticLoading] = useState(true);
    const [addedShippingDemandAnalysisLoading, setAddedShippingDemandAnalysisLoading] = useState(true);
    const [shipOrderDataAnalysisLoading, setShipOrderDataAnalysisLoading] = useState(true);
    const [hotShipRouteRankLoading, setHotShipRouteRankLoading] = useState(true);
    const [hotShipDestinationPortRankLoading, setHotShipDestinationPortRankLoading] = useState(true);
    const [shipInfoServiceFeeStatisticLoading, setShipInfoServiceFeeStatisticLoading] = useState(true);
    const [addedShipInfoServiceFeeAnalysisLoading, setAddedShipInfoServiceFeeAnalysisLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        setPurchaseOverviewLoading(true);
        void loadPurchaseOverview({ scope: 1 })
            .then(result => {
                if (!disposed) {
                    setPurchaseOverview(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页采购总览数据加载失败");
            })
            .finally(() => {
                if (!disposed) setPurchaseOverviewLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadPurchaseOverview]);

    useEffect(() => {
        let disposed = false;
        setPurchaseServiceCostLoading(true);
        void loadPurchaseServiceCost({ scope: 1 })
            .then(result => {
                if (!disposed) {
                    setPurchaseServiceCost(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页服务费收入数据加载失败");
            })
            .finally(() => {
                if (!disposed) setPurchaseServiceCostLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadPurchaseServiceCost]);

    useEffect(() => {
        let disposed = false;
        setPurchaseServiceCostAdditionLoading(true);
        void loadPurchaseServiceCostAdditionStatistics({ statisticsScope: 2 })
            .then(result => {
                if (!disposed) {
                    setPurchaseServiceCostAdditionStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页新增服务费数据加载失败");
            })
            .finally(() => {
                if (!disposed) setPurchaseServiceCostAdditionLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadPurchaseServiceCostAdditionStatistics]);

    useEffect(() => {
        let disposed = false;
        setPurchaseServiceCostShapeLoading(true);
        void loadPurchaseServiceCostShape({ pieChartScope: 0 })
            .then(result => {
                if (!disposed) {
                    setPurchaseServiceCostShape(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页服务费构成数据加载失败");
            })
            .finally(() => {
                if (!disposed) setPurchaseServiceCostShapeLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadPurchaseServiceCostShape]);

    useEffect(() => {
        let disposed = false;
        setPurchaseServiceCostAdditionAnalysisLoading(true);
        void loadPurchaseServiceCostAdditionAnalysis({ analysisScope: 2 })
            .then(result => {
                if (!disposed) {
                    setPurchaseServiceCostAdditionAnalysis(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页服务费新增分析数据加载失败");
            })
            .finally(() => {
                if (!disposed) setPurchaseServiceCostAdditionAnalysisLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadPurchaseServiceCostAdditionAnalysis]);

    useEffect(() => {
        let disposed = false;
        setAddedMemberCustomerStatisticLoading(true);
        void loadAddMemberCustomerStatistic({ scope: 2 })
            .then(result => {
                if (!disposed) {
                    setAddedMemberCustomerStatistic(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页会员新增统计数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedMemberCustomerStatisticLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddMemberCustomerStatistic]);

    useEffect(() => {
        let disposed = false;
        setShippingDemandStatisticLoading(true);
        void loadShippingDemandStatistic({})
            .then(result => {
                if (!disposed) {
                    setShippingDemandStatistic(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船运需求数据加载失败");
            })
            .finally(() => {
                if (!disposed) setShippingDemandStatisticLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadShippingDemandStatistic]);

    useEffect(() => {
        let disposed = false;
        setAddedShippingDemandStatisticLoading(true);
        void loadAddShippingDemandStatistic({ scope: "2" })
            .then(result => {
                if (!disposed) {
                    setAddedShippingDemandStatistic(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页新增船运需求数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedShippingDemandStatisticLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddShippingDemandStatistic]);

    useEffect(() => {
        let disposed = false;
        setShippingDemandConvertLoading(true);
        void loadShippingDemandConvertAnalysis({ scope: 1 })
            .then(result => {
                if (!disposed) {
                    setShippingDemandConvertAnalysis(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船运需求转化数据加载失败");
            })
            .finally(() => {
                if (!disposed) setShippingDemandConvertLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadShippingDemandConvertAnalysis]);

    useEffect(() => {
        let disposed = false;
        setShipStatisticLoading(true);
        void loadShipStatistic({})
            .then(result => {
                if (!disposed) {
                    setShipStatistic(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船舶统计数据加载失败");
            })
            .finally(() => {
                if (!disposed) setShipStatisticLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadShipStatistic]);

    useEffect(() => {
        let disposed = false;
        setAddedShipStatisticLoading(true);
        void loadAddShipStatistic({ scope: "2" })
            .then(result => {
                if (!disposed) {
                    setAddedShipStatistic(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船舶新增统计数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedShipStatisticLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddShipStatistic]);

    useEffect(() => {
        let disposed = false;
        setAddedShippingDemandAnalysisLoading(true);
        void loadAddShippingDemandAnalysis({ scope: "2" })
            .then(result => {
                if (!disposed) {
                    setAddedShippingDemandAnalysis(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船运需求新增分析数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedShippingDemandAnalysisLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddShippingDemandAnalysis]);

    useEffect(() => {
        let disposed = false;
        setShipOrderDataAnalysisLoading(true);
        void loadShipOrderDataAnalysis({ scope: 3 })
            .then(result => {
                if (!disposed) {
                    setShipOrderDataAnalysis(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船运单分析数据加载失败");
            })
            .finally(() => {
                if (!disposed) setShipOrderDataAnalysisLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadShipOrderDataAnalysis]);

    useEffect(() => {
        let disposed = false;
        setHotShipRouteRankLoading(true);
        void loadHotShipRouteRank({})
            .then(result => {
                if (!disposed) {
                    setHotShipRouteRank(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页热门航线数据加载失败");
            })
            .finally(() => {
                if (!disposed) setHotShipRouteRankLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadHotShipRouteRank]);

    useEffect(() => {
        let disposed = false;
        setHotShipDestinationPortRankLoading(true);
        void loadHotShipDestinationPortRank({})
            .then(result => {
                if (!disposed) {
                    setHotShipDestinationPortRank(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页热门目的地数据加载失败");
            })
            .finally(() => {
                if (!disposed) setHotShipDestinationPortRankLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadHotShipDestinationPortRank]);

    useEffect(() => {
        let disposed = false;
        setShipInfoServiceFeeStatisticLoading(true);
        void loadShipInfoServiceFeeStatistic({})
            .then(result => {
                if (!disposed) {
                    setShipInfoServiceFeeStatistic(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船务信息服务费数据加载失败");
            })
            .finally(() => {
                if (!disposed) setShipInfoServiceFeeStatisticLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadShipInfoServiceFeeStatistic]);

    useEffect(() => {
        let disposed = false;
        setAddedShipInfoServiceFeeAnalysisLoading(true);
        void loadAddShipInfoServiceFeeAnalysis({ scope: 3 })
            .then(result => {
                if (!disposed) {
                    setAddedShipInfoServiceFeeAnalysis(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页船务信息服务费新增数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedShipInfoServiceFeeAnalysisLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddShipInfoServiceFeeAnalysis]);

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        void loadRegisteredCustomerStatistic(registeredDateFilter || {})
            .then(result => {
                if (!disposed) {
                    setStatistics(result);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页统计数据加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadRegisteredCustomerStatistic, registeredDateFilter]);

    useEffect(() => {
        let disposed = false;
        setMemberLoading(true);
        void loadMemberCustomerStatistic(memberDateFilter || {})
            .then(result => {
                if (!disposed) {
                    setMemberStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页会员统计数据加载失败");
            })
            .finally(() => {
                if (!disposed) setMemberLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadMemberCustomerStatistic, memberDateFilter]);

    useEffect(() => {
        let disposed = false;
        setMemberRevenueLoading(true);
        void loadMemberRevenueStatistic(revenueDateFilter || {})
            .then(result => {
                if (!disposed) {
                    setMemberRevenueStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页会员营收数据加载失败");
            })
            .finally(() => {
                if (!disposed) setMemberRevenueLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadMemberRevenueStatistic, revenueDateFilter]);

    useEffect(() => {
        let disposed = false;
        setConversionLoading(true);
        void loadCustomerConvertAnalysis(conversionDateFilter || { scope: 0 })
            .then(result => {
                if (!disposed) {
                    setConversionStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页用户转化数据加载失败");
            })
            .finally(() => {
                if (!disposed) setConversionLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadCustomerConvertAnalysis, conversionDateFilter]);

    useEffect(() => {
        let disposed = false;
        setAddedRegistrationLoading(true);
        void loadAddRegisteredCustomerAnalysis({ scope: 0 })
            .then(result => {
                if (!disposed) {
                    setAddedRegistrationStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页新增注册数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedRegistrationLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddRegisteredCustomerAnalysis]);

    useEffect(() => {
        let disposed = false;
        setAddedMemberRevenueLoading(true);
        void loadAddMemberRevenueAnalysis({ scope: 0 })
            .then(result => {
                if (!disposed) {
                    setAddedMemberRevenueStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页新增会员费用数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedMemberRevenueLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddMemberRevenueAnalysis]);

    useEffect(() => {
        let disposed = false;
        setAddedMemberCustomerLoading(true);
        void loadAddMemberCustomerAnalysis({ scope: 0 })
            .then(result => {
                if (!disposed) {
                    setAddedMemberCustomerStatistics(result);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("首页新增会员用户数据加载失败");
            })
            .finally(() => {
                if (!disposed) setAddedMemberCustomerLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadAddMemberCustomerAnalysis]);

    const addedRegistrationTotal =
        (addedRegistrationStatistics?.addRegisterCustomerNums || []).reduce((total, value) => total + value, 0) +
        (addedRegistrationStatistics?.addRegisterCarrierNums || []).reduce((total, value) => total + value, 0);
    const addedMemberRevenueTotal =
        (addedMemberRevenueStatistics?.totalBaseMemberCosts || []).reduce((total, value) => total + value, 0) +
        (addedMemberRevenueStatistics?.totalSeniorMemberCosts || []).reduce((total, value) => total + value, 0) +
        (addedMemberRevenueStatistics?.totalEnterpriseGradeMemberCosts || []).reduce(
            (total, value) => total + value,
            0
        );
    const addedMemberCustomerTotal =
        (addedMemberCustomerStatistics?.addBaseMemberNums || []).reduce((total, value) => total + value, 0) +
        (addedMemberCustomerStatistics?.addSeniorMemberNums || []).reduce((total, value) => total + value, 0) +
        (addedMemberCustomerStatistics?.addEnterpriseGradeMemberNums || []).reduce((total, value) => total + value, 0);

    /** 沿用 Vue 注册用户漏斗的转化率规则，避免总注册数为 0 时出现 NaN。 */
    const formatCertificationRate = (certificationCount?: number) => {
        const totalRegisterNum = statistics?.totalRegisterNum || 0;
        if (!certificationCount || !totalRegisterNum) return "0%";
        const rate = (certificationCount / totalRegisterNum) * 100;
        return `${Number.isInteger(rate) ? rate : rate.toFixed(2)}%`;
    };
    const personalCertificationRate = formatCertificationRate(statistics?.totalPersonalCertificationNum);
    const organizationCertificationRate = formatCertificationRate(statistics?.totalOrganizationCertificationNum);

    /** 统一沿用 Vue 看板的千分位和精度展示，避免 Statistic 直接输出原始数字。 */
    const formatHomeNumber = (value: number | undefined, decimalPlaces = 0) =>
        formatThousandSeparator(value || 0, decimalPlaces);

    return (
        <main aria-label="admin-home-page" className="flex min-h-full flex-col gap-4 bg-[#f7f8fb] p-4" data-page="home">
            <header className="rounded-lg bg-white px-6 py-4 shadow-sm">
                <h1 className="m-0 text-xl font-semibold text-[#3370ff]">首页</h1>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="m-0 text-lg font-medium text-[#222]">链云用户动态数据统计</h2>
                    <span className="text-sm text-[#666]">数据已更新至 {new Date().toLocaleDateString("zh-CN")}</span>
                </div>
            </header>
            {errorMessage ? <Alert showIcon type="error" message={errorMessage} /> : null}
            <section aria-label="砂石交易概览" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={purchaseOverviewLoading} title="砂石交易概览">
                    <Statistic suffix="万元" title="累计砂石成交金额" value={purchaseOverview?.cumulativeAmount || 0} />
                </Card>
                <Card loading={purchaseOverviewLoading} title="砂石成交规模">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            suffix="万吨"
                            title="累计砂石成交吨数"
                            value={purchaseOverview?.cumulativeTonnage || 0}
                        />
                        <Statistic
                            suffix="个"
                            title="累计项目总量"
                            value={purchaseOverview?.cumulativeProjectCount || 0}
                        />
                    </div>
                </Card>
                <Card loading={purchaseOverviewLoading} title="采购项目构成">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            title="累计联合采购项目量"
                            value={purchaseOverview?.cumulativeJointProjectCount || 0}
                        />
                        <Statistic
                            title="累计直采项目量"
                            value={purchaseOverview?.cumulativeDirectProjectAmount || 0}
                        />
                    </div>
                </Card>
            </section>
            <section aria-label="服务费收入统计" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={purchaseServiceCostLoading} title="服务费收入统计">
                    <Statistic
                        suffix="万元"
                        title="累计预付款充值"
                        value={purchaseServiceCost?.cumulativePrepayment || 0}
                    />
                </Card>
                <Card loading={purchaseServiceCostLoading} title="账单服务费">
                    <Statistic
                        suffix="万元"
                        title="累计账单服务费"
                        value={purchaseServiceCost?.cumulativeServiceCost || 0}
                    />
                </Card>
                <Card loading={purchaseServiceCostLoading} title="已核销服务费">
                    <Statistic
                        suffix="万元"
                        title="累计已核销服务费"
                        value={purchaseServiceCost?.cumulativeRevokedServiceCost || 0}
                    />
                </Card>
            </section>
            <section aria-label="服务费新增统计" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={purchaseServiceCostAdditionLoading} title="服务费新增统计">
                    <Statistic
                        suffix="万元"
                        title="新增预付款充值"
                        value={purchaseServiceCostAdditionStatistics?.newPrepayment || 0}
                    />
                </Card>
                <Card loading={purchaseServiceCostAdditionLoading} title="新增账单服务费">
                    <Statistic
                        suffix="万元"
                        title="新增账单服务费"
                        value={purchaseServiceCostAdditionStatistics?.newServiceCost || 0}
                    />
                </Card>
                <Card loading={purchaseServiceCostAdditionLoading} title="新增已核销服务费">
                    <Statistic
                        suffix="万元"
                        title="新增已核销服务费"
                        value={purchaseServiceCostAdditionStatistics?.newRevokedServiceCost || 0}
                    />
                </Card>
            </section>
            <section aria-label="服务费构成分析" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={purchaseServiceCostShapeLoading} title="服务费构成分析">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            title="未核销服务费"
                            value={purchaseServiceCostShape?.cumulativePieUnrevokedServiceCost || 0}
                        />
                        <Statistic
                            title="已核销服务费"
                            value={purchaseServiceCostShape?.cumulativePieRevokedServiceCost || 0}
                        />
                    </div>
                </Card>
                <Card loading={purchaseServiceCostShapeLoading} title="收费方式构成">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            title="单价收费金额"
                            value={purchaseServiceCostShape?.cumulativeUnitPriceServiceCost || 0}
                        />
                        <Statistic
                            title="一口价收费金额"
                            value={purchaseServiceCostShape?.cumulativeFixedPriceServiceCost || 0}
                        />
                    </div>
                </Card>
                <Card loading={purchaseServiceCostShapeLoading} title="项目类型构成">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            title="直采项目服务费"
                            value={purchaseServiceCostShape?.cumulativeDirectServiceCost || 0}
                        />
                        <Statistic
                            title="联合采购项目服务费"
                            value={purchaseServiceCostShape?.cumulativeJointServiceCost || 0}
                        />
                    </div>
                </Card>
            </section>
            <section aria-label="服务费新增分析" className="grid grid-cols-1 gap-4">
                <Card loading={purchaseServiceCostAdditionAnalysisLoading} title="服务费新增分析">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div aria-label="预付款充值趋势" className="rounded bg-[#edf3ff] p-3">
                            <div className="font-medium">预付款充值趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(purchaseServiceCostAdditionAnalysis?.newPrepaymentAnalysis || []).map(
                                    (value, index) => (
                                        <span key={`prepayment-${index}`} className="text-sm">
                                            {index + 1}：{value}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                        <div aria-label="账单服务费趋势" className="rounded bg-[#effaf2] p-3">
                            <div className="font-medium">账单服务费趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(purchaseServiceCostAdditionAnalysis?.newServiceCostAnalysis || []).map(
                                    (value, index) => (
                                        <span key={`service-cost-${index}`} className="text-sm">
                                            {index + 1}：{value}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                        <div aria-label="已核销服务费趋势" className="rounded bg-[#fff5e8] p-3">
                            <div className="font-medium">已核销服务费趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(purchaseServiceCostAdditionAnalysis?.newRevokedServiceCostAnalysis || []).map(
                                    (value, index) => (
                                        <span key={`revoked-cost-${index}`} className="text-sm">
                                            {index + 1}：{value}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </Card>
            </section>
            <section aria-label="会员用户新增统计" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={addedMemberCustomerStatisticLoading} title="会员用户新增统计">
                    <Statistic
                        suffix="人"
                        title="新增基础会员"
                        value={addedMemberCustomerStatistic?.addBaseMemberNum || 0}
                    />
                </Card>
                <Card loading={addedMemberCustomerStatisticLoading} title="新增高级会员">
                    <Statistic
                        suffix="人"
                        title="新增高级会员"
                        value={addedMemberCustomerStatistic?.addSeniorMemberNum || 0}
                    />
                </Card>
                <Card loading={addedMemberCustomerStatisticLoading} title="新增企业会员">
                    <Statistic
                        suffix="人"
                        title="新增企业会员"
                        value={addedMemberCustomerStatistic?.addEnterpriseGradeMemberNum || 0}
                    />
                </Card>
            </section>
            <section aria-label="船运需求统计" className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card loading={shippingDemandStatisticLoading} title="船运需求统计">
                    <Statistic
                        suffix="个"
                        title="累计船运需求总量"
                        value={shippingDemandStatistic?.totalPlatShippingDemandNum || 0}
                    />
                </Card>
                <Card loading={shippingDemandStatisticLoading} title="累计船运单总量">
                    <Statistic
                        suffix="单"
                        title="累计船运单总量"
                        value={shippingDemandStatistic?.totalShipOrderNum || 0}
                    />
                </Card>
            </section>
            <section aria-label="船舶统计" className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <Card loading={shipStatisticLoading} title="船舶统计">
                    <Statistic suffix="艘" title="累计船舶总量" value={shipStatistic?.shipNum || 0} />
                </Card>
                <Card loading={shipStatisticLoading} title="认证船舶数量">
                    <Statistic suffix="艘" title="认证船舶数量" value={shipStatistic?.shipApplyNum || 0} />
                </Card>
                <Card loading={shipStatisticLoading} title="水尺表维护数量">
                    <Statistic title="水尺表维护数量" value={shipStatistic?.shipWaterGaugeNum || 0} />
                </Card>
                <Card loading={shipStatisticLoading} title="船舶绑定设备数量">
                    <Statistic suffix="台" title="船舶绑定设备数量" value={shipStatistic?.shipBindDeviceNum || 0} />
                </Card>
                <Card loading={shipStatisticLoading} title="船舶设备占有率">
                    <Statistic suffix="%" title="船舶设备占有率" value={shipStatistic?.shipDeviceOccupancyRate || 0} />
                </Card>
            </section>
            <section aria-label="船舶新增统计" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={addedShipStatisticLoading} title="船舶新增统计">
                    <Statistic suffix="艘" title="新增认证船舶数量" value={addedShipStatistic?.addShipApplyNum || 0} />
                </Card>
                <Card loading={addedShipStatisticLoading} title="已录入水尺数据">
                    <Statistic title="已录入水尺数据" value={addedShipStatistic?.addShipWaterGaugeNum || 0} />
                </Card>
                <Card loading={addedShipStatisticLoading} title="新增船舶绑定设备数量">
                    <Statistic
                        suffix="台"
                        title="新增船舶绑定设备数量"
                        value={addedShipStatistic?.addShipBindDeviceNum || 0}
                    />
                </Card>
            </section>
            <section aria-label="船运需求新增分析" className="grid grid-cols-1 gap-4">
                <Card loading={addedShippingDemandAnalysisLoading} title="船运需求新增分析">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div aria-label="船运需求趋势" className="rounded bg-[#edf3ff] p-3">
                            <div className="font-medium">船运需求趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(addedShippingDemandAnalysis?.platShippingDemandNums || []).map((value, index) => (
                                    <span key={`shipping-demand-${index}`} className="text-sm">
                                        {index + 1}：{value}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div aria-label="船运单总量趋势" className="rounded bg-[#fff5e8] p-3">
                            <div className="font-medium">船运单总量趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(addedShippingDemandAnalysis?.shipOrderNums || []).map((value, index) => (
                                    <span key={`ship-order-${index}`} className="text-sm">
                                        {index + 1}：{value}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            </section>
            <section aria-label="船运单数据分析" className="grid grid-cols-1 gap-4">
                <Card loading={shipOrderDataAnalysisLoading} title="船运单数据分析">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div aria-label="运货吨数趋势" className="rounded bg-[#edf3ff] p-3">
                            <div className="font-medium">运货吨数趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(shipOrderDataAnalysis?.freightTons || []).map((value, index) => (
                                    <span key={`freight-ton-${index}`} className="text-sm">
                                        {index + 1}：{value}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div aria-label="船运费趋势" className="rounded bg-[#fff5e8] p-3">
                            <div className="font-medium">船运费趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(shipOrderDataAnalysis?.shipOrderCosts || []).map((value, index) => (
                                    <span key={`ship-order-cost-${index}`} className="text-sm">
                                        {index + 1}：{value}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            </section>
            <section aria-label="热门航线排行" className="grid grid-cols-1 gap-4">
                <Card loading={hotShipRouteRankLoading} title="热门航线排行">
                    <div className="space-y-2">
                        {hotShipRouteRank.length === 0 ? (
                            <span className="text-sm text-[#999]">暂无数据</span>
                        ) : (
                            hotShipRouteRank.map((item, index) => (
                                <div
                                    key={item.shipRouteId}
                                    className="flex items-center justify-between rounded bg-[#f7f8fb] px-3 py-2"
                                >
                                    <span>
                                        {index + 1}. {item.shipRouteSource} → {item.shipRouteDestination}
                                    </span>
                                    <span className="font-medium">{item.num}</span>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            </section>
            <section aria-label="热门目的地排行" className="grid grid-cols-1 gap-4">
                <Card loading={hotShipDestinationPortRankLoading} title="热门目的地排行">
                    <div className="space-y-2">
                        {hotShipDestinationPortRank.length === 0 ? (
                            <span className="text-sm text-[#999]">暂无数据</span>
                        ) : (
                            hotShipDestinationPortRank.map((item, index) => (
                                <div
                                    key={item.destinationPortId}
                                    className="flex items-center justify-between rounded bg-[#f7f8fb] px-3 py-2"
                                >
                                    <span>
                                        {index + 1}. {item.destinationPortName}
                                    </span>
                                    <span className="font-medium">{item.num}</span>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            </section>
            <section aria-label="船务信息服务费统计" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={shipInfoServiceFeeStatisticLoading} title="船务信息服务费统计">
                    <Statistic
                        suffix="万元"
                        title="累计船务信息服务费"
                        value={shipInfoServiceFeeStatistic?.totalShipInfoServiceFeeNum || 0}
                    />
                </Card>
                <Card loading={shipInfoServiceFeeStatisticLoading} title="货主船务信息服务费">
                    <Statistic
                        suffix="万元"
                        title="货主船务信息服务费"
                        value={shipInfoServiceFeeStatistic?.totalOwnerShipInfoServiceFeeNum || 0}
                    />
                </Card>
                <Card loading={shipInfoServiceFeeStatisticLoading} title="船主船务信息服务费">
                    <Statistic
                        suffix="万元"
                        title="船主船务信息服务费"
                        value={shipInfoServiceFeeStatistic?.totalCaptainShipInfoServiceFeeNum || 0}
                    />
                </Card>
            </section>
            <section aria-label="船务信息服务费新增分析" className="grid grid-cols-1 gap-4">
                <Card loading={addedShipInfoServiceFeeAnalysisLoading} title="船务信息服务费新增分析">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div aria-label="货主船务信息服务费趋势" className="rounded bg-[#edf3ff] p-3">
                            <div className="font-medium">货主船务信息服务费趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(addedShipInfoServiceFeeAnalysis?.addOwnerShipInfoServiceFeeNums || []).map(
                                    (value, index) => (
                                        <span key={`owner-fee-${index}`} className="text-sm">
                                            {index + 1}：{value}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                        <div aria-label="船主船务信息服务费趋势" className="rounded bg-[#fff5e8] p-3">
                            <div className="font-medium">船主船务信息服务费趋势</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(addedShipInfoServiceFeeAnalysis?.addCaptainShipInfoServiceFeeNums || []).map(
                                    (value, index) => (
                                        <span key={`captain-fee-${index}`} className="text-sm">
                                            {index + 1}：{value}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </Card>
            </section>
            <section aria-label="船运需求新增统计" className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card loading={addedShippingDemandStatisticLoading} title="船运需求新增统计">
                    <Statistic
                        suffix="个"
                        title="新增船运需求"
                        value={addedShippingDemandStatistic?.addPlatShippingDemandNum || 0}
                    />
                </Card>
                <Card loading={addedShippingDemandStatisticLoading} title="新增船运单总量">
                    <Statistic
                        suffix="单"
                        title="新增船运单总量"
                        value={addedShippingDemandStatistic?.addShipOrderNum || 0}
                    />
                </Card>
            </section>
            <section aria-label="船运需求转化分析" className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <Card loading={shippingDemandConvertLoading} title="船运需求转化分析">
                    <Statistic
                        title="船运需求总量"
                        suffix="个"
                        value={shippingDemandConvertAnalysis?.addPlatShippingDemandNum || 0}
                    />
                </Card>
                <Card loading={shippingDemandConvertLoading} title="新增船运单">
                    <Statistic
                        title="新增船运单"
                        suffix="个"
                        value={shippingDemandConvertAnalysis?.addShipOrderNum || 0}
                    />
                </Card>
                <Card loading={shippingDemandConvertLoading} title="需求转化率">
                    <Statistic
                        title="平台需求转化率"
                        suffix="%"
                        value={shippingDemandConvertAnalysis?.platShippingDemandConvertRate || 0}
                    />
                </Card>
                <Card loading={shippingDemandConvertLoading} title="船运单转化率">
                    <Statistic
                        title="船运单转化率"
                        suffix="%"
                        value={shippingDemandConvertAnalysis?.shipOrderConvertRate || 0}
                    />
                </Card>
            </section>
            <section aria-label="注册用户统计" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={loading} title="注册用户统计">
                    <HomeDateFilterControl testId="home-registered-date-range" onChange={setRegisteredDateFilter} />
                    <Statistic title="累计注册用户" value={formatHomeNumber(statistics?.totalRegisterNum)} />
                </Card>
                <Card loading={loading} title="用户构成">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic title="链云采购商" value={formatHomeNumber(statistics?.totalRegisterCustomerNum)} />
                        <Statistic title="船务承运商" value={formatHomeNumber(statistics?.totalRegisterCarrierNum)} />
                    </div>
                </Card>
                <Card loading={loading} title="认证转化">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            title="累计个人认证"
                            value={formatHomeNumber(statistics?.totalPersonalCertificationNum)}
                        />
                        <Statistic
                            title="累计组织机构认证"
                            value={formatHomeNumber(statistics?.totalOrganizationCertificationNum)}
                        />
                    </div>
                </Card>
            </section>
            <section aria-label="注册用户漏斗" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card title="注册用户漏斗">
                    <Statistic title="累计注册用户" value={statistics?.totalRegisterNum || 0} />
                </Card>
                <Card title="个人认证">
                    <Statistic title="累计个人认证" value={statistics?.totalPersonalCertificationNum || 0} />
                    <div className="mt-2 text-sm text-[#666]">个人认证转化率：{personalCertificationRate}</div>
                </Card>
                <Card title="组织机构认证">
                    <Statistic title="累计组织机构认证" value={statistics?.totalOrganizationCertificationNum || 0} />
                    <div className="mt-2 text-sm text-[#666]">组织认证转化率：{organizationCertificationRate}</div>
                </Card>
            </section>
            <section aria-label="会员用户统计" className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <Card loading={memberLoading} title="会员用户统计">
                    <HomeDateFilterControl testId="home-member-date-range" onChange={setMemberDateFilter} />
                    <Statistic
                        title="累计会员用户"
                        suffix="人"
                        value={formatHomeNumber(memberStatistics?.totalMemberNum)}
                    />
                </Card>
                <Card loading={memberLoading} title="会员构成">
                    <div className="grid grid-cols-3 gap-4">
                        <Statistic title="基础会员" value={formatHomeNumber(memberStatistics?.totalBaseMemberNum)} />
                        <Statistic title="高级会员" value={formatHomeNumber(memberStatistics?.totalSeniorMemberNum)} />
                        <Statistic
                            title="企业会员"
                            value={formatHomeNumber(memberStatistics?.totalEnterpriseGradeMemberNum)}
                        />
                    </div>
                </Card>
            </section>
            <section aria-label="会员营收统计" className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card loading={memberRevenueLoading} title="会员营收统计">
                    <HomeDateFilterControl testId="home-revenue-date-range" onChange={setRevenueDateFilter} />
                    <Statistic
                        suffix="万元"
                        title="累计会员总费用"
                        value={formatHomeNumber(memberRevenueStatistics?.totalMemberCost, 2)}
                    />
                </Card>
                <Card loading={memberRevenueLoading} title="会员费用构成">
                    <div className="grid grid-cols-3 gap-4">
                        <Statistic
                            title="基础会员"
                            value={formatHomeNumber(memberRevenueStatistics?.totalBaseMemberCost, 2)}
                        />
                        <Statistic
                            title="高级会员"
                            value={formatHomeNumber(memberRevenueStatistics?.totalSeniorMemberCost, 2)}
                        />
                        <Statistic
                            title="企业会员"
                            value={formatHomeNumber(memberRevenueStatistics?.totalEnterpriseGradeMemberCost, 2)}
                        />
                    </div>
                </Card>
            </section>
            <section aria-label="用户转化分析" className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card loading={conversionLoading} title="用户转化分析">
                    <HomeDateFilterControl testId="home-conversion-date-range" onChange={setConversionDateFilter} />
                    <Statistic
                        suffix="%"
                        title="会员转化率"
                        value={formatHomeNumber(conversionStatistics?.memberConvertRate, 2)}
                    />
                </Card>
                <Card loading={conversionLoading} title="转化用户数">
                    <div className="grid grid-cols-2 gap-4">
                        <Statistic
                            title="注册用户"
                            suffix="人"
                            value={formatHomeNumber(conversionStatistics?.registerNum)}
                        />
                        <Statistic
                            title="会员用户"
                            suffix="人"
                            value={formatHomeNumber(conversionStatistics?.memberNum)}
                        />
                    </div>
                </Card>
            </section>
            <section aria-label="注册用户新增分析" className="grid grid-cols-1 gap-4">
                <Card loading={addedRegistrationLoading} title="注册用户新增分析">
                    <Statistic title="新增注册用户" value={addedRegistrationTotal} />
                    <div aria-label="新增注册用户趋势数据" className="mt-4 flex flex-wrap gap-2">
                        {(addedRegistrationStatistics?.addRegisterCustomerNums || []).map((value, index) => (
                            <span key={`customer-${index}`} className="rounded bg-[#edf3ff] px-2 py-1 text-sm">
                                {index + 1}月：{value}
                            </span>
                        ))}
                    </div>
                </Card>
            </section>
            <section aria-label="会员费用新增分析" className="grid grid-cols-1 gap-4">
                <Card loading={addedMemberRevenueLoading} title="会员费用新增分析">
                    <Statistic suffix="万元" title="新增会员费用" value={addedMemberRevenueTotal} />
                    <div aria-label="新增会员费用趋势数据" className="mt-4 flex flex-wrap gap-2">
                        {(addedMemberRevenueStatistics?.totalBaseMemberCosts || []).map((value, index) => (
                            <span key={`revenue-${index}`} className="rounded bg-[#effaf2] px-2 py-1 text-sm">
                                {index + 1}月：{value}
                            </span>
                        ))}
                    </div>
                </Card>
            </section>
            <section aria-label="会员用户新增分析" className="grid grid-cols-1 gap-4">
                <Card loading={addedMemberCustomerLoading} title="会员用户新增分析">
                    <Statistic title="新增会员用户" value={addedMemberCustomerTotal} />
                    <div aria-label="新增会员用户趋势数据" className="mt-4 flex flex-wrap gap-2">
                        {(addedMemberCustomerStatistics?.addBaseMemberNums || []).map((value, index) => (
                            <span key={`member-${index}`} className="rounded bg-[#fff5e8] px-2 py-1 text-sm">
                                {index + 1}月：{value}
                            </span>
                        ))}
                    </div>
                </Card>
            </section>
        </main>
    );
}

export default HomePage;
